import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { FormArray, FormGroup, Validators, FormControl } from '@angular/forms';
import { ApiQuoteResponse } from '@bhtp-client-api/base';
import { CoverageViewConfig, AcknowledgementService } from '@bhtp-client-web/component';
import { Observable } from 'rxjs';
import { NavigationStepComponent } from './navigation-step.component';
import { PurchaseNavigationService } from './purchase-navigation.service';
import { PurchaseData } from './../shared';
import { WriteValueOverride } from './models';
import { CoverageContainer, Coverage, Constants } from './../shared';

@Component({
    selector: 'coverages-step',
    template: require('./coverages-step.component.html')
})
export class CoveragesStepComponent extends NavigationStepComponent {
    @Output() public coveragesChanged: EventEmitter<CoverageContainer> = new EventEmitter<CoverageContainer>();

    private quoting: boolean;
    private requoteCFAR: boolean;
    private standardCoverages: Array<Coverage>;
    private extraCoverages: Array<Coverage>;
    private optionalCoverages: Array<Coverage>;
    private formConfiguration: CoverageViewConfig;

    private currentCoverages: CoverageContainer;

    private optionalCoveragesFormArray: FormArray;
    private loadingOptionalCoverages: boolean;

    constructor(navigationService: PurchaseNavigationService) {
        super(navigationService);
    }

    public canContinue(): Observable<boolean> {
        return Observable.of(this.optionalCoveragesFormArray && this.optionalCoveragesFormArray.valid);
    }

    public quoteBeforeContinue(): boolean {
        return true;
    }

    public writeValues(purchaseData: PurchaseData, override: WriteValueOverride = WriteValueOverride.Default): void {
        if (this.optionalCoveragesFormArray && this.optionalCoveragesFormArray.valid) {

            // Combines the list of currently selected coverages (purchaseData) and list of newly selected coverages (optionalCoverages)
            // This is needed so that coverages that are unavailable due to eligibility will remain to get quoted and come back as the user
            // moves back and forth between the different steps
            let currCoverages = this.optionalCoverages.filter(c => c.isSelected);
            let unavailableCoverages = new Array<Coverage>();
            if (purchaseData.selectedCoverages) {
            purchaseData.selectedCoverages.forEach(c => {
                    if (!this.optionalCoverages.find(cc => cc.ratingId === c.ratingId)) {
                        unavailableCoverages.push(c);
                    }
                });
            }
            purchaseData.selectedCoverages = [...currCoverages, ...unavailableCoverages];

            if (this.currentCoverages && this.currentCoverages.rentalInformation) {
                purchaseData.rentalCarPickupDate = this.currentCoverages.rentalInformation.rentalCarPickupDate;
                purchaseData.rentalCarReturnDate = this.currentCoverages.rentalInformation.rentalCarReturnDate;
                purchaseData.carRentalCompany = this.currentCoverages.rentalInformation.carRentalCompany;
            }
        }
    }

    public buildForm(): void {
        this.standardCoverages = [];
        this.extraCoverages = [];

        AcknowledgementService.setAcknowledgementsOnCoverages(this.currentQuote.coverages.included, this.currentPurchaseData.acknowledgements);
        AcknowledgementService.setAcknowledgementsOnCoverages(this.currentQuote.coverages.optional, this.currentPurchaseData.acknowledgements);
        this.standardCoverages = Coverage.createCoverages(this.currentQuote.coverages.included, ['standard'], true);
        this.extraCoverages = Coverage.createCoverages(this.currentQuote.coverages.included, ['extra', 'extra upgrade'], true);
        this.optionalCoverages = Coverage.createCoverages(this.currentQuote.coverages.optional, ['upgrade'], true, this.currentPurchaseData.selectedCoverages);

        // requote CFAR if package is under a new product
        if (this.currentPackage) {
            switch (this.currentPackage.productRatingId) {
                case '1':
                case '2':
                case '3':
                    this.requoteCFAR = false;
                    break;
                default:
                    this.requoteCFAR = true;
            };
        }

        // if coverages selected, requote all coverages so they display, but don't
        // reconcile CFAR's premium. All other pages will requote selected coverages
        if (this.optionalCoverages.some(c => c.isSelected) || (this.currentPurchaseData.selectedCoverages && this.currentPurchaseData.selectedCoverages.length > 0)) {
            this.loadingOptionalCoverages = true;
            this.quote(false, false, null)
                .subscribe(quote => {
                    this.loadingOptionalCoverages = false;
                    this.optionalCoverages = Coverage.createCoverages(this.currentQuote.coverages.optional, ['upgrade'], true, this.currentPurchaseData.selectedCoverages);

                    // quote will factor in CFAR price for all coverages. Since no change was made, use old
                    // value for premium
                    let currentCoverage: Coverage = this.currentPurchaseData.selectedCoverages.find(c => c.ratingId === Constants.COVERAGE_CFAR && c.isSelected);
                    let newQuoteCoverage = this.optionalCoverages.find(c => c.ratingId === Constants.COVERAGE_CFAR);
                    if (this.requoteCFAR && currentCoverage && newQuoteCoverage) {
                        newQuoteCoverage.premium = currentCoverage.premium;
                    }

                    this.setupForm();
                },
                null,
                () => {
                    this.loadingOptionalCoverages = false;
                });
        }
        else {
            this.setupForm();
        }
    }

    public onCoverageChanges(coverages: CoverageContainer): void {
        this.currentCoverages = coverages;
        this.coveragesChanged.emit(coverages);
    }

    private setupForm(): void {
        let tempFormConfig = new CoverageViewConfig();
        tempFormConfig.optionalCoverages = this.optionalCoverages;
        tempFormConfig.carRentalCompany = this.currentPurchaseData.carRentalCompany;
        tempFormConfig.dropoffDate = this.currentPurchaseData.rentalCarReturnDate;
        tempFormConfig.pickupDate = this.currentPurchaseData.rentalCarPickupDate;
        tempFormConfig.tripDepartureDate = this.currentPurchaseData.departureDate;
        tempFormConfig.tripReturnDate = this.currentPurchaseData.returnDate;

        this.formConfiguration = tempFormConfig;
        this.optionalCoveragesFormArray = new FormArray([]);
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/purchase/coverages-step.component.ts